import { Center, Grid, GridItem, HStack, useDisclosure } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { useSkipKyc } from 'api/CompeonReverseApi/operation/queryHooks';
import { Asset } from 'components/Asset';
import { ProgressSectionType } from 'pages/customerPortal/InquiryDetails/Dashboard/types';
import { useToasts } from 'shared/hooks/useToasts';
import { triggerKycAction } from 'store/inquiryDetails/actions';
import { getKycStartedAt, isKycFailed } from 'store/inquiryDetails/selectors';
import { AlertDialogComponent } from 'theme/components/AlertDialog';
import { ButtonComponent } from 'theme/components/Button';
import { HeadingComponent } from 'theme/components/Heading';
import ArrowRightIcon from 'theme/components/Icon/ArrowRightIcon';
import { ProgressSection } from 'theme/components/ProgressSection';
import { TextComponent } from 'theme/components/Text';
import useDispatchApiCall from 'utils/hooks/useDispatchApiCallHook';
import { useTranslations } from 'utils/hooks/useTranslations';

import { useResolveKycDataBank } from '../useOperationProgressSections';

interface Props {
  isFirst?: boolean;
  isLast?: boolean;
  isCompleted?: boolean;
  isPending?: boolean;
  completedValue?: number;
  progressIncrement?: number;
  progressTotal?: number;
  id?: string;
  data: {
    hasError?: boolean;
  };
}

export const TriggerKycStep = ({
  isFirst,
  isLast,
  isCompleted,
  isPending,
  data,
  completedValue,
  progressIncrement,
  progressTotal,
  id,
}: Props) => {
  const t = useTranslations('pages.inquiryDetails.dashboard.actions.triggerKyc');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { id: inquiryId } = useParams<{ id: string }>();
  const { error: notifyError, success } = useToasts();
  const { makeCall, isPending: isApiCallPending } = useDispatchApiCall();
  const noResults = useSelector(isKycFailed);
  const hasKycTriggered = useSelector(getKycStartedAt);
  const {
    visible: resolveKycDataBankStepVisible,
    completedValue: resolveKycDataBankStepCompletedValue,
  } = useResolveKycDataBank();
  const resolveKycDataBankStepCompleted = resolveKycDataBankStepCompletedValue === 100;
  const { mutate: skipKyc, isLoading: isSkipKycLoading } = useSkipKyc();

  const handleTrigger = async () => {
    const { error } = await makeCall(triggerKycAction(inquiryId));

    if (error) {
      notifyError({
        description: t('error'),
      });
    } else {
      success({ description: t('success') });
      onClose();
    }
  };

  const triggerButtonDisabled = noResults || hasKycTriggered;
  const triggerButtonPending =
    (isPending || isApiCallPending) &&
    !(resolveKycDataBankStepVisible && !resolveKycDataBankStepCompleted); // The right part of the condition checks if there are multiple companies to select from but the selection was not done yet

  return (
    <ProgressSection
      isFirst={isFirst}
      isLast={isLast}
      isCompleted={isCompleted}
      completedValue={completedValue}
      progressIncrement={progressIncrement}
      progressTotal={progressTotal}
      id={id}
    >
      <Grid templateColumns={['repeat(1, 1fr)', null, '2fr 1fr']} gap={12}>
        <GridItem>
          <HeadingComponent as="h4" color="brand.default" mb={2} variant="secondary">
            {t('title')}
          </HeadingComponent>
          <TextComponent mb={3}>{t('description')}</TextComponent>
          {data?.hasError ? (
            <TextComponent color="status.error" mb={3}>
              {t('error')}
            </TextComponent>
          ) : null}
          {noResults && !data.hasError ? (
            <TextComponent color="status.tertiary" mb={3}>
              {t('notFound')}
            </TextComponent>
          ) : null}
          {!isCompleted && (
            <HStack>
              <ButtonComponent
                leftIcon={<ArrowRightIcon boxSize={6} display="block" />}
                onClick={onOpen}
                isLoading={triggerButtonPending}
                variant="primary"
                disabled={triggerButtonDisabled}
              >
                {t('action')}
              </ButtonComponent>
              <ButtonComponent
                variant="link"
                onClick={() => skipKyc({ inquiryId })}
                isLoading={isSkipKycLoading}
              >
                {t('skipAction')}
              </ButtonComponent>
            </HStack>
          )}

          <AlertDialogComponent
            cancelText={t('modal.cancelText')}
            confirmText={t('modal.confirmText')}
            isOpen={isOpen}
            onClose={onClose}
            onConfirm={handleTrigger}
            title={t('modal.title')}
          >
            {t('modal.text')}
          </AlertDialogComponent>
        </GridItem>

        <GridItem>
          <Center height="100%">
            <Asset htmlHeight="200" type="dashboard" value={ProgressSectionType.TRIGGER_KYC} />
          </Center>
        </GridItem>
      </Grid>
    </ProgressSection>
  );
};
